/**
 * Authentication Service
 *
 * Handles all authentication-related API calls.
 */

import { apiClient, type ApiResponse } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  CurrentUser,
} from '../types';

/**
 * Login with username, password, and reCAPTCHA token
 * Note: Login endpoint returns { token, refreshToken } directly, not wrapped in ApiResponse
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>(
    API_ENDPOINTS.AUTH.LOGIN,
    credentials
  );
  return response.data;
}

/**
 * Refresh the access token using refresh token
 */
export async function refreshToken(
  request: RefreshTokenRequest
): Promise<ApiResponse<RefreshTokenResponse>> {
  const response = await apiClient.post<ApiResponse<RefreshTokenResponse>>(
    API_ENDPOINTS.AUTH.REFRESH,
    request
  );
  return response.data;
}

/**
 * Get current authenticated user details
 */
export async function getCurrentUser(): Promise<ApiResponse<CurrentUser>> {
  const response = await apiClient.get<ApiResponse<CurrentUser>>(API_ENDPOINTS.AUTH.ME);
  return response.data;
}

/**
 * Logout (if backend has logout endpoint)
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
  } catch {
    // Logout should succeed even if API call fails
    // The client-side cleanup will handle the rest
  }
}

// Export as an object for convenient usage
export const authService = {
  login,
  refreshToken,
  getCurrentUser,
  logout,
};
